<template>
    <div>
        <div class="flex flex-wrap">
            <div class="w-full md:w-3/4 md:pr-20">
                <form>
                    <section>
                        <h1 class="mt-2 mb-4 font-sans-bold">Account information</h1>

                        <div class="mb-12">
                            <label for="organization_name" ref="organization_name">
                                Organization Name <span class="text-red">*</span>
                                <div class="text-sm font-sans-book">
                                If you are an individual not associated with an organization or company, please enter your first and last name in this field.
                                </div>
                            </label>
                            <input
                                id="organization_name"
                                type="text"
                                name="organization_name"
                                v-validate="'required'"
                                v-model="form.organization_name"
                            >
                            <span v-show="errors.has('organization_name')" class="error text-error-red text-lg">{{ errors.first('organization_name') }}</span>
                        </div>

                        <div class="mb-12">
                            <label for="organization_url">
                                Organization/Company Website URL
                            </label>

                            <url-input v-model="form.organization_url"></url-input>
                        </div>

                        <div class="mb-12">
                            <label for="first_name" ref="first_name">
                                First Name <span class="text-red">*</span>
                            </label>
                            <input id="first_name" type="text" name="first_name"
                                v-validate="'required'"
                                v-model="form.first_name">
                            <span v-show="errors.has('first_name')" class="error text-error-red text-lg">{{ errors.first('first_name') }}</span>
                        </div>

                        <div class="mb-12">
                            <label for="last_name" ref="last_name">
                                Last Name <span class="text-red">*</span>
                            </label>
                            <input id="last_name" type="text" name="last_name"
                                v-validate="'required'"
                                v-model="form.last_name">
                            <span v-show="errors.has('last_name')" class="error text-error-red text-lg">{{ errors.first('last_name') }}</span>
                        </div>

                        <div class="mb-12">
                            <label for="email" ref="email">
                                Account Contact Email <span class="text-red">*</span>
                            </label>
                            <input id="email" type="email" name="email"
                                v-validate="'required|email'"
                                v-model="form.email">
                            <span v-show="errors.has('email')" class="error text-error-red text-lg">{{ errors.first('email') }}</span>
                        </div>

                        <div class="mb-12">
                            <label for="phone" ref="phone">
                                Account Contact Phone Number <span class="text-red">*</span>
                            </label>
                            <input id="phone" type="text" name="phone"
                                v-validate="'required'"
                                v-model="form.phone">
                            <span v-show="errors.has('phone')" class="error text-error-red text-lg">{{ errors.first('phone') }}</span>
                        </div>

                        <div class="mb-12">
                            <label for="password" ref="password">
                                Password <span class="text-red">*</span>
                                <div class="text-sm font-sans-book">
                                <span class="font-sans-bold">Password should contain at least 8 characters, including at least 1 lowercase letter, 1 uppercase letter, and 1 number.</span> NOTE: There is only one account associated with an event. Therefore, you might need to share your portal login credentials with others who are working on your event (to help input details, for check-in at your event, etc.) Please be sure to set a password you are comfortable sharing, if necessary.
                                </div>
                            </label>
                            <input id="password" type="password" name="password" ref="passwordInput"
                                v-validate="'required|min:8|upperCase|number|lowerCase'"
                                v-model="form.password"
                            >
                            <span v-show="errors.has('password')" class="error text-error-red text-lg">{{ errors.first('password') }}</span>
                        </div>

                        <div class="mb-12">
                            <label for="password_confirmation" ref="password_confirmation">
                                Password Confirmation <span class="text-red">*</span>
                            </label>
                            <input id="password_confirmation" type="password" name="password_confirmation"
                                v-validate="'required|confirmed:passwordInput'"
                                v-model="form.password_confirmation"
                                data-vv-as="password">
                            <span v-show="errors.has('password_confirmation')" class="error text-error-red text-lg">{{ errors.first('password_confirmation') }}</span>
                        </div>
                    </section>

                    <section>
                        <h1 class="mt-2 mb-4 font-sans-bold">Information for public festival directory</h1>

                        <div class="mb-12">
                            <label for="title" ref="accountType">
                                Do you want to be represented as an individual or as a multi-person organization? <span class="text-red">*</span>
                            </label>
                            <div class="text-sm font-sans-book mb-12">
                                If you select "individual," we will use singular pronouns in website copy. For example, "I am here to reimagine..." For multi-person organizations, we say "We are here to reimagine..."
                            </div>

                            <div class="group__radio">
                                <div class="select-box flex flex-wrap0">

                                    <label class="text-base font-normal w-full md:w-1/2 md:pr-3">
                                        <input name="accountType" type="radio"
                                            v-validate="'required'"
                                            v-model="form.accountType"
                                            value="individual">
                                        <span class="radio inline-block mr-2"></span> Individual
                                    </label>

                                    <label class="text-base font-normal w-full md:w-1/2 md:pr-3">
                                        <input name="accountType" type="radio"
                                            v-validate="'required'"
                                            v-model="form.accountType"
                                            value="organization">
                                        <span class="radio inline-block mr-2"></span> Multi-person organization
                                    </label>

                                </div>
                                <span v-show="errors.has('accountType')" class="error text-error-red text-lg">{{ errors.first('accountType') }}</span>
                            </div>
                        </div>

                        <div class="mb-12">
                            <label for="story" ref="story">
                                What’s your organization’s story? <span class="text-red">*</span>
                                <div class="text-sm font-sans-book mb-12">
                                    900 characters maximum. This is your bio and description, which can include your mission statement, types of resources you provide, and/or what inspired you to do this work. Use I/we when you can; stories are better when they are in your personal voice!
                                </div>
                            </label>

                            <textarea id="story" name="story"
                                v-model="form.story"
                                v-validate="'required|max:900'"></textarea>
                                <div class="text-sm font-sans-italic text-gray-dark mt-1">Character count: {{ storyCount }}</div>
                                <span v-show="errors.has('story')" class="error text-error-red text-lg">{{ errors.first('story') }}</span>
                        </div>

                        <div class="mb-12">
                            <label for="reimagine" ref="reimagine">
                                Finish the following: "What I'm / What we're here to reimagine..." <span class="text-red">*</span>
                                <div class="text-sm font-sans-book mb-12">
                                    240 characters maximum. What is your inspiring vision, mantra, or change you wish to see in the world? Keep this short, sweet, and unique. No need to repeat the phrase above, just complete it midway through the sentence.
                                </div>
                            </label>

                            <textarea id="reimagine" name="reimagine" ref="reimagine"
                                v-model="form.reimagine"
                                v-validate="'required|max:240'"></textarea>
                                <div class="text-sm font-sans-italic text-gray-dark mt-1">Character count: {{ reimagineCount }}</div>
                                <span v-show="errors.has('reimagine')" class="error text-error-red text-lg">{{ errors.first('reimagine') }}</span>
                        </div>

                        <div class="mb-12">
                            <label for="publicPhone">
                                Public Phone Number
                            </label>
                            <input id="publicPhone" type="text" name="publicPhone"
                                v-model="form.publicPhone">
                        </div>

                        <div class="mb-12">
                            <label for="publicEmail">
                                Public Email
                            </label>
                            <input id="publicEmail" type="text" name="publicEmail"
                                v-model="form.publicEmail">
                        </div>

                        <div class="mb-12">
                            <label for="city">
                                City
                            </label>
                            <input id="city" type="text" name="city"
                                v-model="form.city">
                        </div>

                        <div class="mb-12">
                            <label for="state">
                                State
                            </label>
                            <input id="state" type="text" name="state"
                                v-model="form.state">
                        </div>

                        <div class="mb-12">
                            <label for="facebookUrl">
                                Facebook URL
                            </label>

                            <url-input v-model="form.facebookUrl"></url-input>
                        </div>

                        <div class="mb-12">
                            <label for="twitterUrl">
                                Twitter URL
                            </label>

                            <url-input v-model="form.twitterUrl"></url-input>
                        </div>

                        <div class="mb-12">
                            <label for="instagramUrl">
                                Instagram URL
                            </label>

                            <url-input v-model="form.instagramUrl"></url-input>
                        </div>

                        <div class="mb-12">
                            <label for="linkedinUrl">
                                LinkedIn URL
                            </label>

                            <url-input v-model="form.linkedinUrl"></url-input>
                        </div>
                    </section>

                    <section>
                        <h1 class="mt-2 mb-4 font-sans-bold">Terms & Conditions</h1>

                        <div class="mb-12">
                            <label for="password" ref="agreeToTerms">
                                Terms & Conditions <span class="text-red">*</span>
                            </label>
                            <div class="mb-12">
                                <p>By clicking "I agree," I agree that I have reviewed and agree with Reimagine's <a href="https://www.letsreimagine.org/terms-conditions/" target="_blank">Terms &amp; Conditions</a> and <a href="https://www.letsreimagine.org/privacy-cookies/" target="_blank">Privacy Policy</a> and that if I am registering on behalf of an organization, I warrant that I am authorized by the organization to register on its behalf.</p>
                            </div>
                            <div class="select-box">
                                <label class="text-base font-normal">
                                <input type="checkbox" v-model="agreeToTerms" name="agreeToTerms"
                                    v-validate="'required'">
                                    <span class="check inline-block mr-2"></span>I agree
                                </label>
                            </div>
                            <div v-show="errors.has('agreeToTerms')" class="error text-error-red text-lg">{{ errors.first('agreeToTerms') }}</div>
                        </div>
                    </section>

                    <button class="button" @click.stop.prevent="validateRegister" :class="{ 'opacity-50 cursor-not-allowed': processing }" :disabled="processing">
                        <template v-if="processing">Processing...</template>
                        <template v-else>Register</template>
                    </button>
                </form>
            </div>
            <div class="w-full md:w-1/4 border-t-5 border-black pt-3 mt-10 md:mt-2">

                <h2 class="text-lg mb-5 font-sans-bold">Help with Registration</h2>
                <ul class="list-reset mb-10 text-base">
                    <li class="mb-2"><a target="_blank" href="https://docs.google.com/document/d/1CXgEzIgteW5_VsPhhDAM_VevMcSO6mCwOviWILMD8ug/edit?usp=sharing">Profile and Account Guide</a></li>
                </ul>

                <support></support>

                <p>PLEASE NOTE: Your registration for this Collaborator Portal automatically creates your account for our ticketing system through our partner SquadUP. Your login credentials to access SquadUP's mobile app for onsite event registration/check-in will be the same as your portal login.</p>
            </div>
        </div>
    </div>
</template>

<script>
    import UrlInput from "../global/UrlInput.vue";
    import Support from "../global/Support.vue";
    import validation from "../../mixins/validation";

    export default {
        name: "UserRegistration",
        title: "User Registration",
        mixins: [validation],
      created() {
          this.$router.push({ name: 'register' });
      },
      data() {
            return {
                form: {
                    organization_name: "",
                    organization_url: "",
                    first_name: "",
                    last_name: "",
                    phone: "",
                    email: "",
                    password: "",
                    password_confirmation: "",
                    accountType: "",
                    story: "",
                    reimagine: "",
                    publicPhone: "",
                    publicEmail: "",
                    city: "",
                    state: "",
                    facebookUrl: "",
                    twitterUrl: "",
                    instagramUrl: "",
                    linkedinUrl: "",
                },
                agreeToTerms: false,
                processing: false,
            };
        },
        components: {
            UrlInput,
            Support,
        },
        computed: {
            storyCount() {
                return this.form.story ? this.form.story.length : 0;
            },
            reimagineCount() {
                return this.form.reimagine ? this.form.reimagine.length : 0;
            },
        },
        methods: {
            validateRegister() {
                this.$validator.validate().then((result) => {
                    if (!result) {
                        this.gotoFirstValidationError();
                        return;
                    }
                    this.register();
                });
            },
            register() {
                this.processing = true;

                axios
                    .post("/register", this.form)
                    .then((response) => {
                        const field = this.$validator.fields.find({
                            name: "email",
                            scope: this.$options.scope,
                        });

                        if (!response || response.data.message != "OK") {
                            this.$validator.errors.add({
                                id: field.id,
                                field: "email",
                                msg: "Failed to register user",
                                scope: this.$options.scope,
                            });
                        }

                        this.processing = false;

                        localStorage.setItem("isLoggedIn", "true");
                        this.$store.dispatch("getMe").then(() => {
                            window.location.href = "/";
                        });
                    })
                    .catch((error) => {
                        console.log(error);
                        this.processing = false;
                        if (error.response.data.errors) {
                            _.forEach(error.response.data.errors, (message, id) => {
                                const field = this.$validator.fields.find({
                                    name: id,
                                    scope: this.$options.scope,
                                });
                                this.$validator.errors.add({
                                    id: field.id,
                                    field: id,
                                    msg: message[0],
                                    scope: this.$options.scope,
                                });
                            });
                        }
                    });
            },
        },
    };
</script>
